/**
 * TopBar — hamburger, page title, search, notifications, and a
 * professional user chip (avatar + first name + chevron) with account menu.
 */

import { useState, useRef, useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Menu,
  Search,
  LogOut,
  User,
  Settings,
  HelpCircle,
  Blocks,
  ChevronDown,
} from 'lucide-react';
import api from '../../services/api';
import { authApi } from '../../services/api/authApi';
import { storageRemove } from '../../lib/nativeStorage';
import { shortcutLabel } from '../../lib/platform';
import { resolveRouteTitle } from '../../app/routeTitles';
import { NotificationBell } from './NotificationBell';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { OPEN_COMMAND_BAR_EVENT } from '../GlobalCommandBar';
import { markIntentionalLogout } from '../../app/loginRedirect';
import { PROFILE_UPDATED_EVENT, type ProfileUpdatedDetail } from '../../lib/profileUpdatedEvent';
import { useChatStore } from '../../stores/chatStore';
import { t } from '../../i18n';
import { cn } from '@/lib/utils';

interface TopBarProps {
  onToggleSidebar: () => void;
}

function firstNameOf(fullName: string): string {
  const trimmed = fullName.trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0] ?? '';
}

export function TopBar({ onToggleSidebar }: TopBarProps) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // Same source as the window/tab title (hooks/useDocumentTitle).
  const pageTitle = resolveRouteTitle(pathname, search);
  const [userName, setUserName] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [userInitial, setUserInitial] = useState<string | null>(null);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let active = true;
    authApi
      .me()
      .then((res) => {
        if (!active) return;
        const user = res.data?.user;
        const name = user?.name?.trim() ?? '';
        const email = user?.email?.trim() ?? '';
        setUserName(name);
        setUserEmail(email);
        if (name) setUserInitial(name.charAt(0).toUpperCase());
        else if (email) setUserInitial(email.charAt(0).toUpperCase());
        setUserAvatar(user?.avatar ?? null);
      })
      .catch(() => {
        /* leave the generic avatar fallback in place */
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onProfileUpdated = (event: Event): void => {
      const detail = (event as CustomEvent<ProfileUpdatedDetail>).detail;
      if (!detail) return;
      if (detail.name !== undefined) {
        const trimmed = detail.name.trim();
        setUserName(trimmed);
        setUserInitial(trimmed ? trimmed.charAt(0).toUpperCase() : null);
      }
      if (detail.avatar !== undefined) setUserAvatar(detail.avatar);
    };
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
  }, []);

  const handleLogout = () => {
    markIntentionalLogout();
    useChatStore.getState().teardown();
    api.setAuthToken(null);
    api.setWorkspaceId(null);
    storageRemove('buzzbit_refresh_token');
    setUserMenuOpen(false);
    navigate('/login', { replace: true });
  };

  const go = (path: string) => {
    setUserMenuOpen(false);
    navigate(path);
  };

  let avatarEl: ReactNode;
  if (userAvatar) {
    avatarEl = <img src={userAvatar} alt="" className="h-full w-full object-cover" />;
  } else if (userInitial) {
    avatarEl = <span>{userInitial}</span>;
  } else {
    avatarEl = <User className="h-4 w-4" />;
  }

  const firstName = firstNameOf(userName);
  const menuLabel = firstName || t('topbar.account');

  return (
    <>
      <header className="app-titlebar relative z-20 flex h-16 flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 lg:px-6">
        <div className="z-10 flex min-w-0 items-center gap-4">
          <button
            type="button"
            onClick={onToggleSidebar}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100 lg:hidden"
            aria-label={t('topbar.toggleMenu')}
          >
            <Menu className="h-5 w-5 text-gray-500" />
          </button>

          <h1 className="whitespace-nowrap text-base font-semibold tracking-[-0.015em] text-gray-900">
            {pageTitle}
          </h1>
        </div>

        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event(OPEN_COMMAND_BAR_EVENT))}
            aria-label={t('topbar.searchAria')}
            className="pointer-events-auto hidden w-[min(360px,calc(100vw-16rem))] min-w-[280px] items-center gap-2 whitespace-nowrap rounded-md bg-gray-50 px-3 py-2 text-left transition-colors hover:bg-gray-100 md:flex"
          >
            <Search className="h-4 w-4 flex-shrink-0 text-gray-400" />
            <span className="min-w-0 flex-1 truncate whitespace-nowrap text-sm text-gray-400">
              {t('topbar.searchPlaceholder')}
            </span>
            <kbd className="flex-shrink-0 rounded border border-gray-200 bg-white px-2 py-0.5 text-xs font-semibold text-gray-500">
              {shortcutLabel('K')}
            </kbd>
          </button>
        </div>

        <div className="z-10 flex items-center gap-2">
          <NotificationBell />

          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event(OPEN_COMMAND_BAR_EVENT))}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100 md:hidden"
            aria-label={t('topbar.search')}
          >
            <Search className="h-5 w-5 text-gray-500" />
          </button>

          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              aria-label={t('topbar.userMenu')}
              aria-expanded={userMenuOpen}
              aria-haspopup="menu"
              className={cn(
                'flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 transition-colors',
                'hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
                userMenuOpen && 'bg-gray-50',
              )}
            >
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-900 text-sm font-medium text-white">
                {avatarEl}
              </span>
              <span className="hidden max-w-[9rem] truncate text-sm font-medium text-gray-900 sm:inline">
                {menuLabel}
              </span>
              <ChevronDown
                className={cn(
                  'hidden h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-200 sm:block',
                  userMenuOpen && 'rotate-180',
                )}
                aria-hidden
              />
            </button>

            {userMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 z-50 mt-2 w-64 rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
              >
                <div className="border-b border-gray-100 px-4 py-3">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {userName || menuLabel}
                  </p>
                  {userEmail ? (
                    <p className="mt-0.5 truncate text-xs text-gray-500">{userEmail}</p>
                  ) : null}
                </div>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => go('/account')}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <User className="h-4 w-4 text-gray-400" />
                  <span>{t('topbar.account')}</span>
                </button>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => go('/account')}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <Settings className="h-4 w-4 text-gray-400" />
                  <span>{t('topbar.settings')}</span>
                </button>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => go('/account?tab=integrations')}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <Blocks className="h-4 w-4 text-gray-400" />
                  <span>{t('topbar.integrations')}</span>
                </button>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => go('/getting-started')}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                  <span>{t('topbar.gettingStarted')}</span>
                </button>

                <WorkspaceSwitcher onBeforeSwitch={() => setUserMenuOpen(false)} />

                <div className="my-1 border-t border-gray-100" />

                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t('topbar.logOut')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}

export default TopBar;
